import { lazy, Suspense, useEffect, useLayoutEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { initializeAuth } from '@/store/slices/authSlice';
import { setPreviewPreferenceMode } from '@/store/slices/themeSlice';
import { setApiPreviewContext } from '@/lib/api';
import Layout from '@/components/Layout/Layout';
import ErrorBoundary from '@/components/Common/ErrorBoundary';

// ── Static imports ────────────────────────────────────────────────────────────
// These three pages are kept eagerly loaded:
//   LoginPage       — first page any unauthenticated user sees; must be instant.
//   AuthCallbackPage — must stay mounted to catch the OAuth Hub event; a lazy
//                      chunk that loads just-in-time could miss the event.
//   NotFoundPage    — tiny; never worth an async round-trip.
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import PluginAuthPage   from '@/pages/PluginAuthPage';
import PluginCombatPage from '@/pages/PluginCombatPage';
import LoginPage        from '@/pages/LoginPage';
import NotFoundPage     from '@/pages/NotFoundPage';

// ── Lazy-loaded page chunks ───────────────────────────────────────────────────
// Vite creates a separate .js file for each lazy() call. The browser only
// fetches it when the user first navigates to that route, so the initial
// bundle is dramatically smaller. Subsequent navigations hit the HTTP cache.
const LibraryPage                       = lazy(() => import('@/pages/LibraryPage'));
const LibraryWebsiteResourcesPage       = lazy(() => import('@/pages/LibraryWebsiteResourcesPage'));
const LibraryTechOverviewPage           = lazy(() => import('@/pages/LibraryTechOverviewPage'));
const LibraryWorldSystemPage            = lazy(() => import('@/pages/LibraryWorldSystemPage'));
const LibraryCharacterSystemPage        = lazy(() => import('@/pages/LibraryCharacterSystemPage'));
const LibraryCombatSystemPage           = lazy(() => import('@/pages/LibraryCombatSystemPage'));
const LibraryTechOverviewWarbachPage    = lazy(() => import('@/pages/LibraryTechOverviewWarbachPage'));
const LibrarySiteGuidePage              = lazy(() => import('@/pages/LibrarySiteGuidePage'));
const BoardsPage            = lazy(() => import('@/pages/BoardsPage'));
const BoardPage             = lazy(() => import('@/pages/BoardPage'));
const PostPage              = lazy(() => import('@/pages/PostPage'));
const NewPostPage           = lazy(() => import('@/pages/NewPostPage'));
const CharactersPage        = lazy(() => import('@/pages/CharactersPage'));
const CombatPage            = lazy(() => import('@/pages/CombatPage'));
const CharacterCreatorPage  = lazy(() => import('@/pages/CharacterCreatorPage'));
const CharacterSheetPage    = lazy(() => import('@/pages/CharacterSheetPage'));
const Roll20ImportPage        = lazy(() => import('@/pages/Roll20ImportPage'));
const DnDBeyondImportPage     = lazy(() => import('@/pages/DnDBeyondImportPage'));
const CampaignMarketplacePage = lazy(() => import('@/pages/CampaignMarketplacePage'));
const ProfilePage           = lazy(() => import('@/pages/ProfilePage'));
const AdminPage             = lazy(() => import('@/pages/AdminPage'));
const DmDashboardPage       = lazy(() => import('@/pages/DmDashboardPage'));
const WorldsPage            = lazy(() => import('@/pages/WorldsPage'));
const WorldCreatePage       = lazy(() => import('@/pages/WorldCreatePage'));
const WorldDetailPage       = lazy(() => import('@/pages/WorldDetailPage'));
const CampaignCreatePage    = lazy(() => import('@/pages/CampaignCreatePage'));
const CampaignDetailPage    = lazy(() => import('@/pages/CampaignDetailPage'));
const LootChestPage         = lazy(() => import('@/pages/LootChestPage'));
const DonatePage            = lazy(() => import('@/pages/DonatePage'));
const SupportersPage        = lazy(() => import('@/pages/SupportersPage'));
const PrivacyPolicyPage     = lazy(() => import('@/pages/PrivacyPolicyPage'));

// Role hierarchy: admin > moderator = dm > user
// dm and moderator share level 2 — different permissions but equal rank.
const ROLE_LEVEL: Record<string, number> = { user: 1, dm: 2, moderator: 2, admin: 3 };

/**
 * Shown while a lazy page chunk is being fetched. Keeps the Layout shell
 * (nav, sidebar) visible so the page doesn't go blank between navigations.
 */
function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-6 h-6 rounded-full border-2 border-gold/20 border-t-gold animate-spin" />
    </div>
  );
}

/**
 * Redirects to /boards when the user is authenticated, /login otherwise.
 * Used as the index route so the root URL always goes somewhere meaningful.
 * By the time this renders the global isLoading spinner is already gone, so
 * we can read `user` directly without re-checking isLoading here.
 */
function AuthRedirect() {
  const { user } = useAppSelector((s) => s.auth);
  return <Navigate to={user ? '/worlds' : '/login'} replace />;
}

function ProtectedRoute({ children, requireRole, exactRoles }: {
  children: ReactNode;
  /** Minimum role level required (uses hierarchy) */
  requireRole?: 'dm' | 'moderator' | 'admin';
  /** Require the user to have exactly one of these roles */
  exactRoles?: Array<'user' | 'dm' | 'moderator' | 'admin'>;
}) {
  const { user, isLoading, viewAsRole, viewAsUser } = useAppSelector((s) => s.auth);
  const effectiveRole = viewAsUser?.role ?? viewAsRole ?? user?.role;

  if (isLoading) return <div className="flex items-center justify-center h-screen">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  if (requireRole && (ROLE_LEVEL[effectiveRole ?? ''] ?? 0) < (ROLE_LEVEL[requireRole] ?? 99)) {
    return <Navigate to="/" replace />;
  }
  if (exactRoles && !exactRoles.includes(effectiveRole as any)) {
    return <Navigate to="/" replace />;
  }

  // Suspense boundary: catches the async chunk load for any lazy page inside.
  // PageFallback keeps the Layout shell visible while the chunk downloads.
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

export default function App() {
  const dispatch = useAppDispatch();
  const { isLoading, viewAsRole, viewAsUser } = useAppSelector((s) => s.auth);
  const { themeId }   = useAppSelector((s) => s.theme);
  const location = useLocation();

  // Restore session from Amplify on every fresh page load.
  useEffect(() => { dispatch(initializeAuth()); }, [dispatch]);

  useLayoutEffect(() => {
    setApiPreviewContext({
      userId: viewAsUser?.userId,
      role: viewAsUser?.role ?? viewAsRole ?? undefined,
    });
    dispatch(setPreviewPreferenceMode(!!(viewAsUser || viewAsRole)));
  }, [dispatch, viewAsRole, viewAsUser]);

  // Keep html[data-theme] in sync with the Redux theme slice.
  // The anti-FOUC script in index.html sets the initial value synchronously,
  // so this effect only fires on subsequent user-driven changes.
  useEffect(() => {
    if (themeId === 'darkfeld') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', themeId);
    }
  }, [themeId]);

  // Keep html font-size in sync with the UI scale preference.
  // 100 % = 16 px (browser default) — clear the override so the page
  // inherits the browser's default and respects user OS font settings.
  const uiScale = useAppSelector((s) => s.theme.uiScale);
  useEffect(() => {
    if (uiScale === 100) {
      document.documentElement.style.removeProperty('font-size');
    } else {
      document.documentElement.style.fontSize = `${(uiScale / 100) * 16}px`;
    }
  }, [uiScale]);

  // Show a full-screen spinner while we check for an existing session so that
  // ProtectedRoute never flickers to /login on a valid returning user.
  // Exceptions — routes that manage their own loading state and must stay
  // mounted regardless of the global auth check:
  //   /auth/callback  — must receive the Hub signInWithRedirect event;
  //                     unmounting it during the OAuth code exchange loses
  //                     the event and leaves the user stuck.
  //   /auth/plugin    — iframe page for the browser extension; mounts first
  //                     then calls initializeAuth() itself. Blocking it here
  //                     means the iframe never progresses past the splash screen.
  const AUTH_BYPASS = ['/auth/callback', '/auth/plugin', '/plugin/combat', '/privacy'];
  if (isLoading && !AUTH_BYPASS.includes(location.pathname)) {
    return (
      <div className="flex items-center justify-center h-screen bg-darkbg relative overflow-hidden">
        <div className="absolute inset-0 bg-gold-radial pointer-events-none" />
        <div className="relative text-center space-y-4">
          <div className="relative mx-auto w-16 h-16">
            <div className="absolute inset-0 rounded-full bg-gold/10 blur-xl animate-pulse" />
            <div className="relative w-16 h-16 rounded-full bg-darkbg-card border border-gold/30 flex items-center justify-center">
              <span className="font-display text-gold text-lg font-bold tracking-widest">D</span>
            </div>
          </div>
          <p className="font-display text-2xl text-gold tracking-widest drop-shadow-[0_0_12px_rgba(201,168,76,0.4)]">
            DARKFELD
          </p>
          <p className="text-parchment-muted/60 text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes — no auth required, excluded from isLoading guard via AUTH_BYPASS */}
      <Route path="/login"   element={<LoginPage />} />
      <Route path="/privacy" element={
        <Suspense fallback={<div className="min-h-screen bg-darkbg" />}>
          <PrivacyPolicyPage />
        </Suspense>
      } />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/plugin"   element={<PluginAuthPage />} />
      <Route path="/plugin/combat" element={<PluginCombatPage />} />

      {/* App layout routes */}
      <Route path="/" element={<Layout />}>
        {/* Index: authenticated → boards, unauthenticated → login (landing page) */}
        <Route index element={<AuthRedirect />} />

        {/* Library of Nix — shared document repository */}
        <Route path="library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />

        {/* Library of Nix — Website Resources (static architecture docs) */}
        {/* Note: specific sub-routes are declared before the hub route so React Router
            doesn't accidentally match "website-resources" as part of the hub's own path. */}
        <Route path="library/website-resources/tech-overview/warbach"
               element={<ProtectedRoute><LibraryTechOverviewWarbachPage /></ProtectedRoute>} />
        <Route path="library/website-resources/tech-overview"
               element={<ProtectedRoute><LibraryTechOverviewPage /></ProtectedRoute>} />
        <Route path="library/website-resources/world-system"
               element={<ProtectedRoute><LibraryWorldSystemPage /></ProtectedRoute>} />
        <Route path="library/website-resources/character-system"
               element={<ProtectedRoute><LibraryCharacterSystemPage /></ProtectedRoute>} />
        <Route path="library/website-resources/combat-system"
               element={<ProtectedRoute><LibraryCombatSystemPage /></ProtectedRoute>} />
        <Route path="library/website-resources/user-guide"
               element={<ProtectedRoute><LibrarySiteGuidePage /></ProtectedRoute>} />
        <Route path="library/website-resources"
               element={<ProtectedRoute><LibraryWebsiteResourcesPage /></ProtectedRoute>} />

        {/* Message Boards — require authentication */}
        <Route path="boards" element={<ProtectedRoute><BoardsPage /></ProtectedRoute>} />
        <Route path="boards/:boardId" element={<ProtectedRoute><BoardPage /></ProtectedRoute>} />
        <Route path="boards/:boardId/new" element={<ProtectedRoute><NewPostPage /></ProtectedRoute>} />
        <Route path="boards/:boardId/posts/:postId" element={<ProtectedRoute><PostPage /></ProtectedRoute>} />

        {/* Auth-required routes */}
        <Route path="characters" element={<ProtectedRoute><CharactersPage /></ProtectedRoute>} />
        <Route path="combat" element={<ProtectedRoute><CombatPage /></ProtectedRoute>} />
        <Route path="characters/new" element={<ProtectedRoute><CharacterCreatorPage /></ProtectedRoute>} />
        <Route path="characters/import/roll20"      element={<ProtectedRoute><Roll20ImportPage /></ProtectedRoute>} />
        <Route path="characters/import/dndbeyond"   element={<ProtectedRoute><DnDBeyondImportPage /></ProtectedRoute>} />
        <Route path="characters/:characterId" element={<ProtectedRoute><ErrorBoundary label="CharacterSheetPage"><CharacterSheetPage /></ErrorBoundary></ProtectedRoute>} />
        <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

        {/* Worlds & Campaigns */}
        <Route path="worlds" element={<ProtectedRoute><WorldsPage /></ProtectedRoute>} />
        <Route path="worlds/new" element={<ProtectedRoute requireRole="dm"><WorldCreatePage /></ProtectedRoute>} />
        <Route path="worlds/:worldId" element={<ProtectedRoute><WorldDetailPage /></ProtectedRoute>} />
        <Route path="worlds/:worldId/campaigns/new" element={<ProtectedRoute requireRole="dm"><CampaignCreatePage /></ProtectedRoute>} />
        <Route path="campaigns/:campaignId" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
        <Route path="campaigns/:campaignId/lootchest" element={<ProtectedRoute><LootChestPage /></ProtectedRoute>} />
        <Route path="campaigns/:campaignId/marketplace/:marketplaceId" element={<ProtectedRoute><CampaignMarketplacePage /></ProtectedRoute>} />

        {/* DM dashboard — restricted to dm and admin */}
        <Route path="dm" element={<ProtectedRoute exactRoles={['dm', 'admin']}><DmDashboardPage /></ProtectedRoute>} />

        {/* Donations — site support via Stripe Checkout */}
        <Route path="donate" element={<ProtectedRoute><DonatePage /></ProtectedRoute>} />
        <Route path="donate/supporters" element={<ProtectedRoute><SupportersPage /></ProtectedRoute>} />

        {/* Admin */}
        <Route path="admin" element={<ProtectedRoute requireRole="admin"><AdminPage /></ProtectedRoute>} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
